import { MODEL_TYPE } from '@trustero/trustero-api-web/lib/common/model_pb'
import {
  CSV_VALIDATIONS,
  ParseColumnType,
  RowValidator,
} from 'src/Utils/csv/csv.constants'

export const getFrameworkSectionsCsvParsers = (
  validControlModelIdSet: Set<string>,
  existingRequirementNames: string[] = [],
  existingSectionNames: string[] = [],
): ParseColumnType => ({
  sectionName: {
    validator: (columnName, value, row) => {
      const lengthValidation = CSV_VALIDATIONS.LENGTH(
        columnName,
        value.trim(),
        1,
        100,
        row,
      )
      if (lengthValidation) return lengthValidation
      const normalizedName = value.trim().toLowerCase()
      const exists = existingSectionNames.some(
        (existingName) => existingName.trim().toLowerCase() === normalizedName,
      )
      if (!exists) return null
      return `Row ${row}: section '${value.trim()}' already exists in this framework.`
    },
    mutator: (value: string) => value.trim().slice(0, 100),
  },
  sectionDetails: {
    validator: (columnName, value, row) =>
      CSV_VALIDATIONS.LENGTH(columnName, value.trim(), 0, 2000, row),
    mutator: (value: string) => value.trim().slice(0, 2000),
  },
  requirementId: {
    validator: (columnName, value, row) => {
      const lengthValidation = CSV_VALIDATIONS.LENGTH(
        columnName,
        value.trim(),
        1,
        255,
        row,
      )
      if (lengthValidation) return lengthValidation
      const normalizedName = value.trim().toLowerCase()
      const exists = existingRequirementNames.some(
        (existingName) => existingName.trim().toLowerCase() === normalizedName,
      )
      if (!exists) return null
      return `Row ${row}: requirement '${value.trim()}' already exists in this framework.`
    },
    mutator: (value: string) => value.trim().slice(0, 255),
  },
  requirementDetails: {
    validator: (columnName, value, row) =>
      CSV_VALIDATIONS.LENGTH(columnName, value.trim(), 0, 2000, row),
    mutator: (value: string) => value.trim().slice(0, 2000),
  },
  controlIds: {
    validator: (_, value, row) =>
      CSV_VALIDATIONS.ID_EXISTS(
        value,
        validControlModelIdSet,
        row,
        MODEL_TYPE.CONTROL,
      ) || CSV_VALIDATIONS.ID_EXISTS_ONCE(row, value, MODEL_TYPE.CONTROL),
    mutator: (value: string) =>
      value
        .split(',')
        .map((v) => v.trim())
        .filter(Boolean),
  },
})

const validateUniqueRequirementName = (): RowValidator => {
  const seen = new Map<string, number>()
  return (row, rowNumber) => {
    const raw = row.requirementId?.trim() ?? ''
    if (!raw) return null
    const key = raw.toLowerCase()
    const firstRow = seen.get(key)
    if (firstRow !== undefined) {
      return `Row ${rowNumber}: Duplicate requirement name '${raw}' found. This value was already used in row ${firstRow}.`
    }
    seen.set(key, rowNumber)
    return null
  }
}

const validateConsistentSectionDescription = (): RowValidator => {
  const seen = new Map<string, { description: string; rowNumber: number }>()
  return (row, rowNumber) => {
    const sectionName = (row.sectionName ?? '').trim()
    if (!sectionName) return null
    const description = (row.sectionDetails ?? '').trim()
    const key = sectionName.toLowerCase()
    const prev = seen.get(key)
    if (prev && prev.description !== description) {
      return `Row ${rowNumber}: section_details for section '${sectionName}' does not match row ${prev.rowNumber}. All rows sharing a section must use the same section_details.`
    }
    if (!prev) seen.set(key, { description, rowNumber })
    return null
  }
}

export const getFrameworkSectionsRowValidators = (): RowValidator[] => [
  validateUniqueRequirementName(),
  validateConsistentSectionDescription(),
]

/**
 * Parses the gRPC error message returned when a requirement name collides — either within
 * the request or with a requirement already in the compliance framework.
 */
export const parseDuplicateRequirementNameError = (
  errMessage: string,
): { name: string; isExisting: boolean } | null => {
  const existingMatch = errMessage.match(
    /framework requirement name already exists:\s*([^\n]+)/,
  )
  if (existingMatch) {
    return { name: existingMatch[1].trim(), isExisting: true }
  }
  const dupMatch = errMessage.match(
    /duplicate framework requirement name:\s*([^\n]+)/,
  )
  if (dupMatch) {
    return { name: dupMatch[1].trim(), isExisting: false }
  }
  return null
}

/**
 * Parses the gRPC error message returned when a section name collides with another section in
 * the same compliance framework.
 */
export const parseDuplicateSectionNameError = (
  errMessage: string,
): string | null => {
  const existingMatch = errMessage.match(
    /framework section name already exists:\s*([^\n]+)/,
  )
  if (existingMatch) return existingMatch[1].trim()
  const dupMatch = errMessage.match(
    /duplicate framework section name:\s*([^\n]+)/,
  )
  if (dupMatch) return dupMatch[1].trim()
  return null
}
